/* istanbul ignore file */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { flow, get } from 'lodash';
import Container from 'reactstrap/lib/Container';
import { TrackingHandler } from 'client/tracking/handler';
import { FeaturesSpecsMap } from 'client/tracking/maps/features-specs/features-specs';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildPopularStylesPath, buildVehiclePath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { PAGE_DISPLAY_NAME, URL_PATH } from 'site-modules/features-specs/constants/features-specs';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { PricingCarouselFooter } from 'site-modules/shared/components/core-page/pricing-carousel/pricing-carousel-footer';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { StyleFeaturesSpecs } from 'site-modules/features-specs/components/style-features-specs/style-features-specs.plat-4078-chal1';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';

function FeaturesSpecsUI({ params: pageParams, popularStyles, vehicle, isMobile, pageName }) {
  const defaultStyleId = pageParams.styleId || get(popularStyles, '[0].id');
  const [styleId, setStyleId] = useState(defaultStyleId);

  if (!styleId && defaultStyleId) {
    setStyleId(defaultStyleId);
  }

  useEffect(() => {
    TrackingHandler.useMap(FeaturesSpecsMap);
  }, []);

  const onStyleChange = useCallback(({ styleId: newStyleId }) => {
    setStyleId(newStyleId);
  }, []);

  if (!popularStyles || !styleId || !vehicle.pubStates) {
    return (
      <main id="main-content" className="features-specs">
        <Container>Loading&hellip;</Container>
      </main>
    );
  }
  const selectedStyle = popularStyles.find(({ id }) => id === parseInt(pageParams.styleId, 10));
  const trimName = selectedStyle ? selectedStyle.trimName : '';
  const trim = selectedStyle ? `${pageParams.model}|${trimName.toLowerCase()}` : '';
  const params = { ...pageParams, isUsed: isUsedPubState(vehicle.pubStates), trim };

  const vehicleName = buildDisplayVehicle({
    makeModelSubmodelYear: vehicle,
    params,
    style: selectedStyle,
    showTrim: true,
  });

  return (
    <main id="main-content" className="features-specs">
      <Experiment name="plat-4078" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal1" />
        <Recipe name="chal2" />
        <Recipe name="chal3" />
        <Recipe name="chal4" />
        <Recipe name="chal5" />
      </Experiment>
      <StyleFeaturesSpecs
        vehicle={vehicle}
        styleId={styleId}
        popularStyles={popularStyles}
        vehicleName={vehicleName}
        params={params}
        onStyleChange={onStyleChange}
        pageName={pageName}
        isMobile={isMobile}
      />

      <Container className="mb-1_25">
        {!params.isUsed && (
          <MinHeightWrapper componentType="SUGGESTED_PRICING_CAROUSEL">
            <PricingCarouselFooter
              params={params}
              srpParams={{ ...params, inventorytype: 'new' }}
              className="mt-2 mt-md-0"
              dataTrackingLeadForm={getLeadFormCreativeId({
                isNewVin: true,
                pageNameLegacy: pageName,
                formName: LEAD_FORMS_CREATIVE_ID.FEATURE_SPECS,
              })}
            />
          </MinHeightWrapper>
        )}
        <CoreBreadcrumbs
          className="ps-0 mb-2"
          vehicle={vehicle}
          breadcrumbsParams={{
            params,
            pageDisplayName: PAGE_DISPLAY_NAME,
            urlPath: getUrlPathFromParams(params, URL_PATH),
          }}
        />
        <PageLinksWidget divideSections={false} title={`Related ${vehicleName} info`} className="mt-2 mt-md-0 pb-3" />
      </Container>
    </main>
  );
}

FeaturesSpecsUI.propTypes = {
  params: FeaturesSpecsParams.isRequired,
  isMobile: PropTypes.bool,
  popularStyles: VehicleEntities.PopularStyles,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  pageName: PropTypes.string,
};

FeaturesSpecsUI.defaultProps = {
  popularStyles: null,
  isMobile: false,
  vehicle: null,
  pageName: null,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const FeaturesSpecs = flow(
  component =>
    connectToModel(component, {
      popularStyles: bindToPath(({ params, vehicle }) => !!vehicle && buildPopularStylesPath(params), VehicleModel),
    }),
  component =>
    connectToModel(component, {
      vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
    }),
  connect(mapStateToProps)
)(FeaturesSpecsUI);
